import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog'; // kitlint-allow-material: opened by the shared PayableActionsService, whose callers live outside payables-dashboard
import { MarkAsPaidFormComponent } from '../../mark-as-paid/mark-as-paid-form.component';
import { PayableReviewComponent } from '../../payable-review/payable-review.component';
import { GrowlerService } from '../../../../shared/growler/growler.service';
import { PayablesService } from '../../../../../services/payables/payables.service';
import { Transaction } from '../../../../../models/entities/transactions/transaction';
import { Payable } from '../../../../../models/entities/payable';
import { finalize, take } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import moment from 'moment-timezone';
import { PayableActionType } from '../../../../../enums/payables/payable-action.enum';

export interface MarkAsPaidDialogInput {
    title?: string;
    buttonText?: string;
    payableId?: number;
    vendorName: string;
    invoiceTotal: number;
    paymentAmount: number;
    scheduledPaymentDate?: moment.Moment;
    hideDisclaimer?: boolean;
    expenseCategories?: any[];
    invoiceNumber?: string;
    invoiceDate?: moment.Moment;
    dueDate?: moment.Moment;
    memo?: string;
    payable?: Payable;
}

export interface MarkAsPaidDialogResult {
    success: boolean;
    paymentDate?: string;
}

@UntilDestroy()
@Component({
    selector: 'app-mark-as-paid-dialog',
    templateUrl: './mark-as-paid-dialog.component.html',
    styleUrls: ['./mark-as-paid-dialog.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        PayableReviewComponent,
        MarkAsPaidFormComponent,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class MarkAsPaidDialogComponent implements OnInit {
    isSubmitting = false;
    potentialTransactionMatches: Transaction[] = [];

    @ViewChild(MarkAsPaidFormComponent) markAsPaidFormComponent: MarkAsPaidFormComponent;

    constructor(
            private _dialogRef: MatDialogRef<MarkAsPaidDialogComponent>, // kitlint-allow-material: opened by the shared PayableActionsService, whose callers live outside payables-dashboard
            private _growlerService: GrowlerService,
            private _payableService: PayablesService,
            @Inject(MAT_DIALOG_DATA) public data: MarkAsPaidDialogInput, // kitlint-allow-material: opened by the shared PayableActionsService, whose callers live outside payables-dashboard
    ) {
        this._dialogRef.addPanelClass('kit-dialog');
    }

    get dialogTitle() {
        return this.data.title || 'Mark invoice as paid';
    }

    get buttonText() {
        return this.data.buttonText || 'Mark as paid';
    }

    ngOnInit(): void {
        if (this.data.payable) {
            this.loadPotentialTransactionMatches();
        }
    }

    loadPotentialTransactionMatches(): void {
        this.isSubmitting = true;
        this._payableService.getPotentialTransactionMatches(this.data.payable)
                .pipe(
                        untilDestroyed(this),
                        finalize(() => this.isSubmitting = false)
                )
                .subscribe({
                    next: (matches: Transaction[]) => {
                        this.potentialTransactionMatches = matches;
                    },
                    error: () => {
                        this._growlerService.oops('There was a problem loading potential transaction matches.');
                    },
                });
    }

    cancel(): void {
        this._dialogRef.close();
    }

    markAsPaid(): void {
        if (this.isSubmitting) {
            return;
        }
        if (!this.markAsPaidFormComponent || !this.markAsPaidFormComponent.isValid()) {
            this._growlerService.error('Error', 'Please complete all required fields.');
            return;
        }

        this.isSubmitting = true;

        const markAsPaidFormGroup = this.markAsPaidFormComponent.getFormGroup();
        const paymentDate = this.markAsPaidFormComponent.getPaymentDate();
        const requestData = {
            organizationId: this.data.payable.originalModel.organizationId,
            markAsPaid: {
                ...markAsPaidFormGroup.getRawValue(),
                bankAccountForTransaction: markAsPaidFormGroup.get('bankAccountForTransaction').value?.id,
            },
            payableAction: {
                invoiceAction: PayableActionType.MARK_PAID as const,
                paymentDate: paymentDate ? moment(paymentDate).format('YYYY-MM-DD') : null,
            },
        };

        this._payableService.updatePayablePaymentMethodPostApproval(this.data.payable.id, requestData).pipe(
                finalize(() => this.isSubmitting = false),
                take(1),
        ).subscribe({
            next: () => {
                this._growlerService.success('Success', 'Invoice marked as paid successfully.');
                this._dialogRef.close(<MarkAsPaidDialogResult>{
                    success: true,
                });
            },
            error: () => {
                this._growlerService.oops('There was a problem marking the invoice as paid.');
            }
        });
    }
}
